import React, { useCallback, useEffect, useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import { Frame, Loader, useSageBreakpoint } from '@kajabi/sage-react';
import { useStripeForm } from 'common/components/StripeForm/context';
import { trackOptimizelyEvents } from 'common/services/product_analytics';
import currency from 'currency.js';
import omit from 'lodash/omit';
import pick from 'lodash/pick';

import {
  LEAD_CREATED,
  SIGNUP_PAGE_LOADED,
  SIGNUP_SUBMIT_BUTTON_CLICKED,
} from '../helpers/constants';
import kajabiLogo from '../images/kajabi-logo.svg';
import * as CheckoutService from '../services/CheckoutService';

import { CheckoutContext } from './CheckoutContext';

const REQUIRED_PARAMS = ['first_name', 'last_name', 'email'];
const NON_SOCIAL_REQUIRED_PARAMS = ['password', 'password_confirmation'];

const useFocus = () => {
  const htmlElRef = React.useRef(null);
  const setFocus = () => htmlElRef.current && htmlElRef.current.focus();

  return [htmlElRef, setFocus];
};

export const CheckoutProvider = ({ children }) => {
  const {
    billingDetails: { postal_code: postalCode, country: billingCountry } = {},
    createPaymentMethod,
    elements,
    stripe,
  } = useStripeForm();
  const breakpoints = useSageBreakpoint();
  const signupIpAddress = localStorage.getItem('ip_ip');

  const [planInfo, setPlanInfo] = useState();
  const [twoStepsSignup, setTwoStepsSignup] = useState(false);
  const [twoStepsSignupStep, setTwoStepsSignupStep] = useState('first');
  const [submittingForm, setSubmittingForm] = useState(false);
  const [formParams, setFormParams] = useState({
    signup_country: localStorage.getItem('ip_country'),
    signup_ip_address: signupIpAddress,
    time_zone: Intl.DateTimeFormat().resolvedOptions().timeZone,
    payment_type: 'credit_card',
  });
  const [errors, setErrors] = useState({});
  const [social, setSocial] = useState(false);
  const [marketingParams, setMarketingParams] = useState({});
  const [expressCheckoutEnabled, setExpressCheckoutEnabled] = useState(false);
  const [minified, setMinified] = useState(false);

  const [socialGoogleEnabled, setSocialGoogleEnabled] = useState(false);
  const [auth0ClientId, setAuth0ClientId] = useState();
  const [auth0ClientDomain, setAuth0ClientDomain] = useState();

  const [tax, setTax] = useState();
  const [successfulExpressCheckout, setSuccessfullExpressCheckout] = useState(false);
  const [optimizelyId, setOptimizelyId] = useState(null);

  const [inputPasswordRef, setInputPasswordFocus] = useFocus();

  const isAnnual = useMemo(() => !!planInfo?.plan?.annual_downgrade_id, [planInfo]);

  const isTwoStepsSignup = () => twoStepsSignup === true || twoStepsSignup === 'true';
  const isTwoStepsSignupFirstStep = () => twoStepsSignup && twoStepsSignupStep === 'first';
  const isTwoStepsSignupSecondStep = () => twoStepsSignup && twoStepsSignupStep === 'second';

  const handleFormChange = ({ target: { value, id } }) => {
    setFormParams((f) => ({ ...f, [id]: value }));
    setErrors((e) => omit(e, id));
  };

  const validateEmail = async () => {
    try {
      await CheckoutService.validateEmail(formParams.email);
    } catch ({ response: { data: { message } = {} } }) {
      if (message) {
        setErrors((f) => ({ ...f, email: (f.email || []).concat([message]) }));
      }
    }
  };

  const validateTwoStepsSignupFirstStep = async () => {
    const tempErrors = {};
    REQUIRED_PARAMS.forEach((param) => {
      if (!formParams[param]) {
        tempErrors[param] = ["can't be blank"];
      }
    });

    setErrors((f) => ({ ...f, ...tempErrors }));
    await validateEmail();
    return !Object.keys(tempErrors).length;
  };

  const validateFields = async (params) => {
    const tempErrors = {};
    REQUIRED_PARAMS.concat(social ? [] : NON_SOCIAL_REQUIRED_PARAMS).forEach((param) => {
      if (!params[param]) {
        tempErrors[param] = ["can't be blank"];
      }
    });

    if (!social) {
      ['password', 'password_confirmation'].forEach((param) => {
        if (!!params[param] && params[param].length < 10) {
          tempErrors[param] = (tempErrors[param] || []).concat(
            'is too short (minimum is 10 characters)',
          );
        }
      });

      if (params.password !== params.password_confirmation) {
        tempErrors.password_confirmation = (tempErrors.password_confirmation || []).concat(
          'This value should be the same.',
        );
      }
    }

    await validateEmail();

    if (!params.payment_method_id && !planInfo.cardless) {
      const { error, paymentMethod } = await createPaymentMethod();
      if (error) {
        tempErrors.base = [error.message];
      }
      params.payment_method_id = paymentMethod?.id;
    }
    setErrors((f) => ({ ...f, ...tempErrors }));
    return !Object.keys(tempErrors).length && (planInfo.cardless || !!params.payment_method_id);
  };

  const submitFormTwoStepFirstStep = async () => {
    trackOptimizelyEvents(SIGNUP_SUBMIT_BUTTON_CLICKED);
    setErrors({});
    setSubmittingForm(true);
    const canSubmit = await validateTwoStepsSignupFirstStep();

    try {
      if (canSubmit) {
        const { errors: responseErrors, status } = await CheckoutService.createLeadData({
          accounts_signup: formParams,
        });

        if (responseErrors) {
          setErrors(responseErrors);
          setSubmittingForm(false);
        }

        if (status === 'success') {
          trackOptimizelyEvents(LEAD_CREATED);

          setTwoStepsSignupStep('second');
          setSubmittingForm(false);
          setTimeout(() => {
            setInputPasswordFocus();
          }, 1000);
        }
      } else {
        setSubmittingForm(false);
      }
    } catch (err) {
      setErrors({ base: ['Error submitting form; please try again later.'] });
      setSubmittingForm(false);
    }
  };

  const submitForm = async ({ formParamsOverride = {} }) => {
    trackOptimizelyEvents(SIGNUP_SUBMIT_BUTTON_CLICKED);
    setErrors({});
    setSubmittingForm(true);
    const params = {
      ...formParams,
      postal_code: postalCode,
      signup_country: billingCountry,
      ...formParamsOverride,
    };
    trackAnalytics('Sign Up Submitted', { payment_type: params.payment_type });
    const canSubmit = await validateFields(params);
    try {
      if (canSubmit) {
        const { errors: responseErrors, redirect_url: redirectUrl } =
          await CheckoutService.createSubscription({
            accounts_signup: params,
          });
        const planInfoPrice = currency(planInfo?.plan?.price).value;
        const planInfoConversionValue = planInfoPrice * 100;

        if (responseErrors) {
          setErrors(responseErrors);
          setSubmittingForm(false);
          trackAnalytics('Signed Up Failed', { payment_type: params.payment_type });
        }
        if (redirectUrl) {
          // eslint-disable-next-line no-extra-boolean-cast
          if (!!window.AnalyticsBase) {
            window.AnalyticsBase.trackEvent('Trial Started (Client Side)', {
              email: params?.email,
              plan_name: planInfo?.plan.display_name,
              signup_source: params?.signup_source,
              utm_source: marketingParams?.utm_source,
              utm_medium: marketingParams?.utm_medium,
              utm_campaign: marketingParams?.utm_campaign,
              utm_term: marketingParams?.utm_term,
              utm_content: marketingParams?.utm_content,
              referrer_url: marketingParams?.leadsource,
              marketing_form_name: marketingParams?.marketing_form_name,
            });
          }
          window.location.href = redirectUrl;
        }
      } else {
        setSubmittingForm(false);
      }
    } catch (err) {
      setErrors({ base: ['Error submitting form; please try again later.'] });
      setSubmittingForm(false);
    }
  };

  const getUrlParam = (paramName) => {
    const urlParams = new URLSearchParams(window.location.search);
    return urlParams.get(paramName);
  };

  const calculateTax = useCallback(
    (params) => {
      if (params.billingCountry) {
        CheckoutService.calculateTax({
          postal_code: params.postalCode,
          signup_ip_address: signupIpAddress,
          signup_country: params.billingCountry,
        }).then((data) => {
          setTax(data.tax);
        });
      }
    },
    [signupIpAddress],
  );

  const trackAnalytics = (eventName, data = {}) =>
    window.AnalyticsBase?.trackEvent(eventName, {
      ...data,
      optimizely_id: optimizelyId,
    });

  const handleExpressCheckout = async ({ billingDetails, expressPaymentType }) => {
    trackAnalytics('Express Checkout Confirmed', { payment_type: expressPaymentType });
    const { address, email, name } = billingDetails;
    setErrors({});
    setSubmittingForm(true);

    calculateTax({ postalCode: address.postal_code, billingCountry: address.country });

    const { error, paymentMethod } = await stripe.createPaymentMethod({
      elements,
      params: {
        billing_details: billingDetails,
      },
    });
    if (error) {
      setErrors({ base: error.message }); // TODO: check what error message is
      setSubmittingForm(false);
      return;
    }

    const [split, firstName, lastName] = name.split(/(^.*\s{1})/);
    const newFormParams = {
      ...formParams,
      payment_method_id: paymentMethod.id,
      postal_code: address.postal_code,
      signup_country: address.country,
      payment_type: expressPaymentType,
      email: formParams.email || email,
      first_name: formParams.first_name || firstName,
      last_name: formParams.last_name || lastName,
    };
    if (social) {
      submitForm({ formParamsOverride: newFormParams });
    } else {
      setFormParams(newFormParams);
      setSuccessfullExpressCheckout(true);
      setSubmittingForm(false);
    }
  };

  const contextValue = {
    errors,
    formParams,
    handleFormChange,
    isAnnual,
    planInfo,
    social,
    submitForm,
    submittingForm,
    submitFormTwoStepFirstStep,
    tax,
    twoStepsSignup,
    setTwoStepsSignup,
    twoStepsSignupStep,
    inputPasswordRef,
    getUrlParam,
    socialGoogleEnabled,
    auth0ClientId,
    auth0ClientDomain,
    isTwoStepsSignup,
    isTwoStepsSignupFirstStep,
    isTwoStepsSignupSecondStep,
    marketingParams,
    expressCheckoutEnabled,
    minified,
    successfulExpressCheckout,
    setErrors,
    handleExpressCheckout,
    trackAnalytics,
  };

  useEffect(() => {
    CheckoutService.getPlanInfo().then(
      ({
        initial_form_params: initialFormParams,
        social: socialLogin,
        two_steps_signup: twoStepsSignupValue,
        social_google_enabled: withSocialGoogleValue,
        auth0_client_id: auth0ClientIdValue,
        auth0_client_domain: auth0ClientDomainValue,
        marketing_params: allowedMarketingParams,
        express_checkout_enabled: expressCheckoutEnabledValue,
        minified: minifiedValue,
        optimizely_id: optimizelyIdValue,
        ...rest
      }) => {
        setFormParams((f) => ({
          ...f,
          ...initialFormParams,
        }));
        setSocial(socialLogin);
        setPlanInfo(rest);
        setSocialGoogleEnabled(withSocialGoogleValue);
        setAuth0ClientId(auth0ClientIdValue);
        setAuth0ClientDomain(auth0ClientDomainValue);
        setTwoStepsSignup(twoStepsSignupValue);

        const urlParams = new URLSearchParams(window.location.search);
        setMarketingParams(pick(Object.fromEntries(urlParams.entries()), allowedMarketingParams));
        setExpressCheckoutEnabled(expressCheckoutEnabledValue);
        setMinified(minifiedValue);
        setOptimizelyId(optimizelyIdValue);
      },
    );
  }, []);

  useEffect(() => {
    trackOptimizelyEvents(SIGNUP_PAGE_LOADED);
    // kexpe=twosteps
    const kajabiExperience = getUrlParam('kexpe');
    if (kajabiExperience === 'twosteps') {
      setTwoStepsSignup(true);
    }
  }, []);

  useEffect(() => {
    calculateTax({ postalCode, billingCountry });
  }, [calculateTax, postalCode, billingCountry]);

  return (
    <CheckoutContext.Provider value={contextValue}>
      <Frame
        align={Frame.ALIGNMENTS[planInfo ? 'TOP_LEFT' : 'TOP_CENTER']}
        gap={Frame.GAPS[breakpoints.sm ? 'SM' : 'LG']}
        padding={Frame.PADDINGS[breakpoints.sm ? 'SM' : 'XL']}
        style={{ maxWidth: 1400, margin: 'auto' }}
      >
        {planInfo ? (
          <>
            <img alt="kajabi-logo" src={kajabiLogo} style={{ height: '24px' }} />
            {children}
          </>
        ) : (
          <Loader loading />
        )}
      </Frame>
    </CheckoutContext.Provider>
  );
};

CheckoutProvider.propTypes = {
  children: PropTypes.node.isRequired,
};
